import { Suspense, useState } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import Topbar from "./scenes/global/Topbar";
import Login from "./scenes/authenticate/Login";
import Report from "./scenes/report";
import RealtimeReport from "./scenes/realtime-report";
import FileImport from "./scenes/import";
import { CssBaseline, ThemeProvider, IconButton } from "@mui/material";
import { ColorModeContext, themeSettings, useMode } from "./theme";
import PrivateRoute from "./util/PrivateRouter";

import Drawer from '@mui/material/Drawer';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import MailIcon from '@mui/icons-material/Mail';
import { styled, useTheme } from '@mui/material/styles';
import MuiAppBar from '@mui/material/AppBar';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import PsychologyIcon from '@mui/icons-material/Psychology';
import Box from '@mui/material/Box';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import FileImportRealtime from "./scenes/import-file-realtime";
import URLLookup from "./scenes/url-lookup";
import UserList from "./scenes/user-list";
import UserEdit from "./scenes/user-edit";
import UserAdd from "./scenes/user-add";
import ResetPassword from "./scenes/reset-password";
import URLLookupResult from "./scenes/url-lookup-result";
import BrandAndLogo from "./scenes/brand-and-logo";
import ApiAccessStats from "./scenes/api-access-stats";
import AssessmentIcon from '@mui/icons-material/Assessment';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import PageviewIcon from '@mui/icons-material/Pageview';
import ArticleIcon from '@mui/icons-material/Article';
import ExploreIcon from '@mui/icons-material/Explore';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import QueryStatsIcon from "@mui/icons-material/QueryStats"
import { useContextAuthentication } from "./util/useContextAuth";
import { ToastContainer } from 'react-toastify';

const drawerWidth = 240;

const Main = ({ open, hide, children }) => {
  const theme = useTheme();

  const padding =
    location.pathname === "/url-lookup" || (location.pathname === "/" && hide)
      ? 0
      : theme?.spacing(3);

  return (
    <main
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center", // Center content horizontally
        justifyContent: "center", // Center content vertically
        flexGrow: 1,
        padding: padding,
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: `-${drawerWidth}px`,
        ...(open && {
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginLeft: 0,
        }),
      }}
    >
      {children}
    </main>
  );
};

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: `${drawerWidth}px`,
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));


function App() {
  const contextAuth = useContextAuthentication();

  const valueContext = contextAuth?.value,
  isAdmin=!!valueContext?.isAdmin,
  keyUser=valueContext?.key;

  const [theme, colorMode] = useMode();
  const [isSidebar, setIsSidebar] = useState(true);

  const { pathname } = useLocation();
  const shouldShowTopbar = pathname !== '/login' && pathname !== '/login/';
  const shouldShowhHeader = pathname !== '/url-lookup' && (!(location.pathname === '/' && !isAdmin));

  const shouldShowMenu = pathname !== '/login';

  const [open, setOpen] = useState(false);

  const handleDrawerOpen = () => {
    setOpen(!open);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const navigate = useNavigate();

  const handleItemClick = (route) => {
    navigate(route);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <ToastContainer />
      <AppBar position="fixed" open={open}>
        {shouldShowTopbar && <Topbar setIsSidebar={setIsSidebar} className="topbar" handleDrawerOpen={handleDrawerOpen} isAdmin={isAdmin} />}
      </AppBar>
      {shouldShowMenu && 
          <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              zIndex: (theme) => theme.zIndex.drawer - 1,
            },
          }}
          variant="persistent"
          anchor="left"
          open={open}
        >

          <Divider />
          <List>
            <ListItem key={4} disablePadding>
                <ListItemButton onClick={() => handleItemClick('/url-lookup')}>
                  <ListItemIcon>
                    <ExploreIcon />
                  </ListItemIcon>
                  <ListItemText primary='URL Lookup' />
                </ListItemButton>
              </ListItem>
              {isAdmin ? (
                <>
                  <ListItem key={1} disablePadding>
                    <ListItemButton onClick={() => handleItemClick('/')}>
                      <ListItemIcon>
                        <TaskAltIcon />
                      </ListItemIcon>
                      <ListItemText primary='Statistics' />
                    </ListItemButton>
                  </ListItem>
                  <ListItem key={2} disablePadding>
                    <ListItemButton onClick={() => handleItemClick('/realtime')}>
                      <ListItemIcon>
                        <QueryBuilderIcon />
                      </ListItemIcon>
                      <ListItemText primary='Realtime Progress' />
                    </ListItemButton>
                  </ListItem>
                  <ListItem key={3} disablePadding>
                    <ListItemButton onClick={() => handleItemClick('/machine-learning')}>
                      <ListItemIcon>
                        <PsychologyIcon />
                      </ListItemIcon>
                      <ListItemText primary='Machine Learning' />
                    </ListItemButton>
                  </ListItem>
                  <ListItem key={1} disablePadding>
                    <ListItemButton onClick={() => handleItemClick('/import')}>
                      <ListItemIcon>
                        <UploadFileRoundedIcon frontSize="small"/>
                      </ListItemIcon>
                      <ListItemText primary='URLs Import' />
                    </ListItemButton>
                  </ListItem>
                  <ListItem key={1} disablePadding>
                    <ListItemButton onClick={() => handleItemClick('/import-realtime')}>
                      <ListItemIcon>
                        <AssessmentIcon frontSize="small"/>
                      </ListItemIcon>
                      <ListItemText primary='URLs Import Result' />
                    </ListItemButton>
                  </ListItem>
                  <ListItem key={2} disablePadding>
                    <ListItemButton onClick={() => handleItemClick('/import-lookup')}>
                      <ListItemIcon>
                        <PageviewIcon />
                      </ListItemIcon>
                      <ListItemText primary='Import Lookup' />
                    </ListItemButton>
                  </ListItem>
                  <ListItem key={3} disablePadding>
                    <ListItemButton onClick={() => handleItemClick('/url-lookup-result')}>
                      <ListItemIcon>
                        <ArticleIcon />
                      </ListItemIcon>
                      <ListItemText primary='URL Lookup Result' />
                    </ListItemButton>
                  </ListItem>
                  <ListItem key={5} disablePadding>
                    <ListItemButton onClick={() => handleItemClick('/brands-and-logos')}>
                      <ListItemIcon>
                        <InsertEmoticonIcon />
                      </ListItemIcon>
                      <ListItemText primary='Brands & Logos' />
                    </ListItemButton>
                  </ListItem>
                  <ListItem key={6} disablePadding>
                    <ListItemButton onClick={() => handleItemClick('/api-access-stats')}>
                      <ListItemIcon>
                        <QueryStatsIcon />
                      </ListItemIcon>
                      <ListItemText primary='API Access Stats' />
                    </ListItemButton>
                  </ListItem>
                </>
              ) : null}
          </List>
          <Divider />
          <DrawerHeader style={{ position: 'absolute', bottom: 0, left: 0 }}>
            <IconButton onClick={handleDrawerClose}>
              <KeyboardDoubleArrowLeftIcon />
            </IconButton>
            <span style={{ marginLeft: '8px' }}>close sidebar</span>
          </DrawerHeader>
        </Drawer>
      }
      
      <Main open={open} hide={!isAdmin}>
        {shouldShowhHeader && <DrawerHeader />}
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            key={keyUser}
            element={
              <PrivateRoute>
                <Suspense>
                  {isAdmin ? <Report />: <URLLookup />}
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/import"
            element={
              <PrivateRoute>
                <FileImport />
              </PrivateRoute>
            }
          />
          <Route
            path="/realtime"
            element={
              <PrivateRoute>
                <RealtimeReport />
              </PrivateRoute>
            }
          />
          <Route
            path="/import-realtime"
            element={
              <PrivateRoute>
                <FileImportRealtime />
              </PrivateRoute>
            }
          />
           <Route
            path="/url-lookup"
            element={
              <PrivateRoute>
                <URLLookup />
              </PrivateRoute>
            }
          />
          <Route
            path="/user-list"
            element={
              <PrivateRoute>
                <UserList />
              </PrivateRoute>
                
            }
          />
          <Route
            path="/user-edit/:userId"
            element={
              <PrivateRoute>
                <UserEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/user-add/"
            element={
              <PrivateRoute>
                <UserAdd />
              </PrivateRoute>
            }
          />
          <Route
            path="/url-lookup-result"
            element={
              <PrivateRoute>
                <URLLookupResult />
              </PrivateRoute>
            }
          />
          <Route
            path="/brands-and-logos"
            element={
              <PrivateRoute>
                <BrandAndLogo />
              </PrivateRoute>
            }
          />
          
          <Route
            path="/api-access-stats"
            element={
              <PrivateRoute>
                <ApiAccessStats />
              </PrivateRoute>
            }
          />

          <Route
            path="/reset-password"
            element={
              <PrivateRoute>
                <ResetPassword />
              </PrivateRoute>
            }
          />
        </Routes>
      </Main>
    </Box>
  );
}

export default App;
